import React from 'react';
import { Calendar, Users, Briefcase } from 'lucide-react';

/**
 * Компонент для ввода основных параметров проекта трансформации
 */
const ProjectParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calendar className="w-5 h-5 mr-2 text-indigo-500" />
        Параметры проекта и внутренней команды
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Продолжительность проекта */}
        <div>
          <label htmlFor="projectDuration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Продолжительность проекта (мес.)
          </label>
          <input
            type="text"
            id="projectDuration"
            name="projectDuration"
            value={formData.projectDuration}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.projectDuration ? 'input-error' : ''}`}
            placeholder="Введите количество месяцев"
          />
          {errors.projectDuration && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.projectDuration}
            </p>
          )}
        </div>

        {/* Размер внутренней команды */}
        <div>
          <label htmlFor="internalTeamSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Users className="w-4 h-4 mr-2 text-indigo-500" />
            Размер внутренней команды (чел.)
          </label>
          <input
            type="text"
            id="internalTeamSize"
            name="internalTeamSize"
            value={formData.internalTeamSize}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.internalTeamSize ? 'input-error' : ''}`}
            placeholder="Количество сотрудников"
          />
          {errors.internalTeamSize && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.internalTeamSize}
            </p>
          )}
        </div>

        {/* Средняя зарплата */}
        <div>
          <label htmlFor="averageSalary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Briefcase className="w-4 h-4 mr-2 text-indigo-500" />
            Средняя зарплата (руб./мес.)
          </label>
          <input
            type="text"
            id="averageSalary"
            name="averageSalary"
            value={formData.averageSalary}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.averageSalary ? 'input-error' : ''}`}
            placeholder="Средняя зарплата"
          />
          {errors.averageSalary && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.averageSalary}
            </p>
          )}
        </div>

        {/* Процент времени на проект */}
        <div>
          <label htmlFor="allocatedTimePercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Выделенное время на проект (%)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              id="allocatedTimePercentage"
              name="allocatedTimePercentage"
              value={formData.allocatedTimePercentage}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.allocatedTimePercentage ? 'input-error' : ''}`}
              placeholder="Процент рабочего времени"
            />
            <span className="ml-2 text-gray-600 dark:text-gray-400">%</span>
          </div>
          {errors.allocatedTimePercentage && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.allocatedTimePercentage}
            </p>
          )}
        </div>
      </div>

      {/* Готовность организации и масштаб изменений */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Масштаб изменений */}
        <div>
          <label htmlFor="changeSeverity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Масштаб изменений
          </label>
          <select
            id="changeSeverity"
            name="changeSeverity"
            value={formData.changeSeverity}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.changeSeverity ? 'input-error' : ''}`}
          >
            <option value="minor">Незначительные изменения</option>
            <option value="moderate">Умеренные изменения</option>
            <option value="significant">Значительные изменения</option>
            <option value="radical">Радикальные изменения</option>
          </select>
          {errors.changeSeverity && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.changeSeverity}
            </p>
          )}
        </div>

        {/* Готовность организации */}
        <div>
          <label htmlFor="organizationalReadiness" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Готовность организации к изменениям
          </label>
          <select
            id="organizationalReadiness"
            name="organizationalReadiness"
            value={formData.organizationalReadiness}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.organizationalReadiness ? 'input-error' : ''}`}
          >
            <option value="unprepared">Неподготовленная</option>
            <option value="somewhat-prepared">Частично подготовленная</option>
            <option value="prepared">Подготовленная</option>
            <option value="fully-prepared">Полностью подготовленная</option>
          </select>
          {errors.organizationalReadiness && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.organizationalReadiness}
            </p>
          )}
        </div>
      </div>

      {/* Информация о влиянии параметров на стоимость */}
      <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <p className="text-xs text-indigo-700 dark:text-indigo-300">
          <span className="font-medium">Примечание:</span> Продолжительность проекта и размер внутренней команды значительно влияют на общую стоимость трансформации. Масштаб изменений и готовность организации определяют необходимые ресурсы и риски проекта.
        </p>
      </div>
    </div>
  );
};

export default ProjectParametersSection;